/** Client-side signed-rep export dedupe (matches dashboard-apis signedRepsDedupe). */

function normalizePhoneDigits(phone: unknown): string {
  return String(phone ?? "").replace(/\D/g, "")
}

export function signedRepPersonKey(rep: Record<string, unknown>): string {
  const phoneDigits = normalizePhoneDigits(rep.phone)
  if (phoneDigits.length >= 10) return `p:${phoneDigits}`
  const email = String(rep.email ?? "")
    .trim()
    .toLowerCase()
  if (email) return `e:${email}`
  return `r:${String(rep.rep_id ?? "").trim()}`
}

function repIdDedupeRank(repId: unknown): number {
  const s = String(repId ?? "").trim()
  if (!s) return 2
  if (/^R-/i.test(s)) return 1
  return 0
}

/** Prefer numeric rep_id over R-* when phone/email match. */
export function dedupeSignedRepsPreferNumericRepId<T extends Record<string, unknown>>(
  rows: T[]
): T[] {
  const byKey = new Map<string, T>()
  for (const row of rows) {
    const key = signedRepPersonKey(row)
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, row)
      continue
    }
    const existingRank = repIdDedupeRank(existing.rep_id)
    const rowRank = repIdDedupeRank(row.rep_id)
    if (rowRank < existingRank) {
      byKey.set(key, row)
      continue
    }
    if (rowRank > existingRank) continue
    const existingDate = existing.agreement_signed_date
      ? new Date(String(existing.agreement_signed_date)).getTime()
      : 0
    const rowDate = row.agreement_signed_date
      ? new Date(String(row.agreement_signed_date)).getTime()
      : 0
    if (rowDate > existingDate) byKey.set(key, row)
  }
  return Array.from(byKey.values())
}

/**
 * Export query: division + YEARTODATE — omit date (API also skips YTD slice for list + export).
 * Client dedupe below is a safety net; dashboard-apis dedupes on the server.
 */
export function dedupeDivisionBaseNames(divisions: string[]): string[] {
  const seen = new Map<string, string>()
  for (const d of divisions) {
    const base = String(d || "")
      .trim()
      .replace(/\s+\d{4}$/, "")
      .trim()
    if (!base) continue
    const key = base.toLowerCase()
    if (!seen.has(key)) seen.set(key, base)
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b))
}

export function buildSignedRepsExportQuery(
  apiQuery: Record<string, unknown>
): Record<string, unknown> {
  const q: Record<string, unknown> = { ...apiQuery, isDownload: "true" }
  delete q.pageNumber
  delete q.pageSize

  const division = q.division != null ? String(q.division).trim() : ""
  const dateTag = q.date != null ? String(q.date).toUpperCase() : ""
  if (division && dateTag === "YEARTODATE") {
    delete q.date
  }
  return q
}
